import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get leave requests
export const getLeaveRequests = createAsyncThunk(
  "LeaveRequests/getLeaveRequests",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();

      const currentLang = params.language || getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.employee_id)
        queryParams.append("employee_id", params.employee_id);
      if (params.leave_type_id)
        queryParams.append("leave_type_id", params.leave_type_id);
      if (params.status) queryParams.append("status", params.status);
      if (params.start_date)
        queryParams.append("start_date", params.start_date);
      if (params.end_date) queryParams.append("end_date", params.end_date);
      if (params.search) queryParams.append("search", params.search);

      const response = await axios.get(
        `${BASE_URL}/leave-requests?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get leave requests!";
      return rejectWithValue(message);
    }
  },
);

// Add leave request
export const addLeaveRequest = createAsyncThunk(
  "LeaveRequests/addLeaveRequest",
  async (leaveRequestData, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.post(
        `${BASE_URL}/leave-requests`,
        leaveRequestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add leave request!";
      return rejectWithValue(message);
    }
  },
);

// Update leave request
export const updateLeaveRequest = createAsyncThunk(
  "LeaveRequests/updateLeaveRequest",
  async ({ id, leaveRequestData }, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.put(
        `${BASE_URL}/leave-requests/${id}`,
        leaveRequestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update leave request!";
      return rejectWithValue(message);
    }
  },
);

// Delete leave request
export const deleteLeaveRequest = createAsyncThunk(
  "LeaveRequests/deleteLeaveRequest",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/leave-requests/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete leave request!";
      return rejectWithValue(message);
    }
  },
);

// Get leave request details
export const getLeaveRequestDetails = createAsyncThunk(
  "LeaveRequests/getLeaveRequestDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/leave-requests/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get leave request details!";
      return rejectWithValue(message);
    }
  },
);

// Update leave request status
export const updateLeaveRequestStatus = createAsyncThunk(
  "LeaveRequests/updateLeaveRequestStatus",
  async ({ id, status, rejection_reason }, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const body = { status };
      if (status === "rejected" && rejection_reason) {
        body.rejection_reason = rejection_reason;
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.post(
        `${BASE_URL}/leave-requests/${id}/status`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to update leave request status!";
      return rejectWithValue(message);
    }
  },
);
